/**
 * CardioTwinPage — iter628 — Кардиоваскуларен близнак (10-ти).
 * Лог за BP / Resting HR / HRV + snapshot + history.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Heart, Plus } from "lucide-react";
import { toast } from "sonner";
import ModuleRatingButton from "@/components/ModuleRatingButton";  // iter1114.b
import HrvAnalysisPanel from "@/components/HrvAnalysisPanel";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import HemodynamicsCard from "@/components/cardio/HemodynamicsCard";
import OrthostaticPanel from "@/components/cardio/OrthostaticPanel";
import HeartRateZonesCard from "@/components/cardio/HeartRateZonesCard";
import BpRulesCard from "@/components/cardio/BpRulesCard";
import CongestionCard from "@/components/cardio/CongestionCard";
import ExerciseBpCard from "@/components/cardio/ExerciseBpCard";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function CardioTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ systolic_mmhg: "", diastolic_mmhg: "", resting_hr_bpm: "", hrv_ms: "" });
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/cardio-twin/snapshot`),
        axios.get(`${API}/cardio-twin/history?days=30`),
      ]);
      setSnap(s.data);
      setHistory(h.data.entries || []);
    } catch {
      toast.error("Грешка при зареждане.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([, v]) => v !== "" && v !== null)
          .map(([k, v]) => [k, parseFloat(v)])
      );
      if (Object.keys(payload).length === 0) {
        toast.error("Попълни поне едно поле.");
        return;
      }
      await axios.post(`${API}/cardio-twin/log`, payload);
      window.dispatchEvent(new Event("cardio-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success("Записано!");
      setForm({ systolic_mmhg: "", diastolic_mmhg: "", resting_hr_bpm: "", hrv_ms: "" });
      setRefreshKey((k) => k + 1);
      load();
    } catch {
      toast.error("Грешка при запис.");
    } finally {
      setSaving(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="cardio-twin-page">
      <MobileTopBar title="Сърце и съдове" fallbackTo="/dashboard" testid="cardio-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="cardio" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-red-500" />
          <h1 className="text-xl font-bold">{twinLabel("cardio")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Следи кръвното налягане, пулса в покой, HRV и баланса LDL/HDL. Логни редовно за по-точна оценка.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
        ) : (
          <>
            {/* Snapshot */}
            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="cardio-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="cardio-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
              </div>
              <p className="text-sm leading-relaxed mt-2" data-testid="cardio-interpretation">{snap?.interpretation_bg}</p>
              {snap?.latest && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                  {snap.latest.systolic_mmhg != null && <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">BP</div><div className="font-bold">{snap.latest.systolic_mmhg}/{snap.latest.diastolic_mmhg}</div></div>}
                  {snap.latest.resting_hr_bpm != null && <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">Resting HR</div><div className="font-bold">{snap.latest.resting_hr_bpm} bpm</div></div>}
                  {snap.latest.hrv_ms != null && <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">HRV</div><div className="font-bold">{snap.latest.hrv_ms} ms</div></div>}
                </div>
              )}
            </div>

            {/* iter1057 — хемодинамичен слой от вече въведените числа */}
            <div className="mb-4"><HemodynamicsCard isDark={isDark} refreshKey={refreshKey} /></div>

            {/* Log form */}
            <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="cardio-log-form">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Лог</h2>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { k: "systolic_mmhg", label: "Систолно (mmHg)", placeholder: "120" },
                  { k: "diastolic_mmhg", label: "Диастолно (mmHg)", placeholder: "80" },
                  { k: "resting_hr_bpm", label: "Пулс в покой (bpm)", placeholder: "65" },
                  { k: "hrv_ms", label: "HRV (ms)", placeholder: "55" },
                ].map(f => (
                  <label key={f.k} className="flex flex-col gap-1">
                    <span className="text-[10px] opacity-70">{f.label}</span>
                    <input type="number" placeholder={f.placeholder} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      data-testid={`cardio-input-${f.k}`}
                      className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
                  </label>
                ))}
              </div>
              <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white w-full" data-testid="cardio-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Запиши измерване
              </Button>
            </div>

            {/* iter1057 — пулсов резерв, ортостатика, застой и правила за мерене */}
            <div className="space-y-4 mb-4">
              <HeartRateZonesCard isDark={isDark} refreshKey={refreshKey} />
              <OrthostaticPanel isDark={isDark} onSaved={() => setRefreshKey((k) => k + 1)} />
              <ExerciseBpCard isDark={isDark} refreshKey={refreshKey} />
              <CongestionCard isDark={isDark} />
              <BpRulesCard isDark={isDark} />
            </div>

            {/* History */}
            {history.length > 0 && (
              <div className={`p-5 rounded-2xl border ${cardCls}`} data-testid="cardio-history">
                <h2 className="font-bold text-sm mb-3">История (30 дни)</h2>
                <div className="space-y-1.5">
                  {history.map((e, i) => (
                    <div key={i} className={`p-2 rounded-md text-xs flex justify-between gap-2 ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={`cardio-history-${i}`}>
                      <span className="opacity-60">{e.measured_on}</span>
                      <span>
                        {e.systolic_mmhg && e.diastolic_mmhg ? `${e.systolic_mmhg}/${e.diastolic_mmhg}` : ""}
                        {e.resting_hr_bpm ? ` · HR ${e.resting_hr_bpm}` : ""}
                        {e.hrv_ms ? ` · HRV ${e.hrv_ms}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* iter1114.b — Rate-per-feature */}
            <ModuleRatingButton moduleKey="cardio_twin" moduleName="Кардио близнака" isDark={isDark} />

            {/* iter633 — Professional HRV via NeuroKit2 */}
            <div className="mt-4">
              <HrvAnalysisPanel onAnalyzed={() => load()} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
